import { StatsigProviderExpo } from '@statsig/expo-bindings';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import * as Updates from 'expo-updates';
import * as React from 'react';
import { LogBox, Platform, StyleSheet } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import 'react-native-get-random-values';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { enableScreens } from 'react-native-screens';
import { Provider } from 'react-redux';

import { AppStatsigUser } from './components/AppStatsigUser';
import ErrorBoundary from './components/errorBoundaries/ErrorBoundary';
import { PlentifulServiceContextProvider } from './components/services/PlentifulServiceContext';
import { ReviewServiceContextProvider } from './components/services/ReviewServiceContext';
import { EnvVars } from './constants/EnvConfig';
import MainContainer from './navigation/MainContainer';
import { ExpoModulesProvider } from './providers/ExpoModulesProvider';
import { TranslationProvider } from './providers/TranslationProvider';
import { ErrorScreen } from './screens/ErrorScreen';
import Store from './store/Store';
import { logError } from './utils/logger';

let Sentry;
if (Platform.OS === 'web') {
  Sentry = require('@sentry/react');
} else {
  Sentry = require('@sentry/react-native');
}

enableScreens();

// Suppress LogBox during testing when REACT_NATIVE_LOGBOX_DISABLED is set
if (__DEV__) {
  if (process.env.REACT_NATIVE_LOGBOX_DISABLED === 'true') {
    console.log('LogBox is DISABLED for testing');
    LogBox.ignoreAllLogs(true);
  } else {
    console.log('LogBox is ENABLED');
  }
}

// Hotfix for react-native-reanimated see: https://github.com/software-mansion/react-native-reanimated/issues/3355#issuecomment-1518942329
if (typeof window !== 'undefined') {
  window._frameTimestamp = null;
}

if (EnvVars.useSentry) {
  // initialize sentry
  Sentry.init({
    dsn: EnvVars.sentryDsn,
    environment: EnvVars.sentryEnv,
    debug: EnvVars.sentryEnv != 'production',
    enableLogs: true,
  });
}

const styles = StyleSheet.create({
  view: {
    flex: 1,
  },
});

const restartApp = async () => {
  if (Platform.OS === 'web') {
    location.reload();
  } else {
    try {
      await Updates.reloadAsync();
    } catch (error) {
      logError(new Error('Failed to reload app', { cause: error }));
    }
  }
};

function renderErrorScreenFallback(ErrorScreenComponent) {
  // Lowercase name is deliberate: ErrorBoundary invokes this as a plain function
  // (fallback({ isChunkLoadError })), not as a rendered element, so it never gets a
  // component instance — a hook called in here would throw "Invalid hook call".
  return function errorScreenFallback({ isChunkLoadError }) {
    return <ErrorScreenComponent restartCallback={restartApp} isUpdateAvailable={isChunkLoadError} />;
  };
}

function App({
  MainContainerComponent = MainContainer,
  StatsigProviderComponent = StatsigProviderExpo,
  ErrorScreenComponent = ErrorScreen,
} = {}) {
  const expoModules = {
    Updates,
    Constants,
    Device,
    Notifications,
  };

  const mainContainerWithErrorBoundary = (
    <ErrorBoundary fallback={renderErrorScreenFallback(ErrorScreenComponent)}>
      <ExpoModulesProvider modules={expoModules}>
        <TranslationProvider>
          <PlentifulServiceContextProvider envVars={EnvVars}>
            <ReviewServiceContextProvider>
              <GestureHandlerRootView style={styles.view}>
                <MainContainerComponent />
              </GestureHandlerRootView>
            </ReviewServiceContextProvider>
          </PlentifulServiceContextProvider>
        </TranslationProvider>
      </ExpoModulesProvider>
    </ErrorBoundary>
  );

  return (
    <Provider store={Store}>
      <SafeAreaProvider>
        <ErrorBoundary fallback={mainContainerWithErrorBoundary}>
          <AppStatsigUser
            StatsigProviderComponent={StatsigProviderComponent}
            sdkKey={EnvVars.statsigSdkKey}
            options={{ environment: { tier: EnvVars.statsigEnv } }}
          >
            {mainContainerWithErrorBoundary}
          </AppStatsigUser>
        </ErrorBoundary>
      </SafeAreaProvider>
    </Provider>
  );
}

export default App;
